'use client'

import React, { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { useLanguage } from '@/context/LanguageContext'
import { GET_PAGES_TREE } from '../pageQueries'

const GRID_COLUMNS = '1fr 200px 200px 1fr 110px'

interface PageProfileNode {
  id?: string
  language: string
  slug?: string
  title?: string
}

interface PageTreeNode {
  id: string
  code: string
  published: boolean
  arrangement: number
  parentId: string | null
  page_profiles: PageProfileNode[]
}

export default function PagesTree({ searchTerm }: { searchTerm: string }) {
  const { language: currentLanguage } = useLanguage()
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const { data, loading } = useQuery(GET_PAGES_TREE, {
    fetchPolicy: 'cache-and-network',
  })

  const pages: PageTreeNode[] = useMemo(
    () => data?.findManyPage ?? [],
    [data]
  )

  // Build lookup maps from the flat list (reuses PageParentSelect's approach)
  const { byId, childrenByParent } = useMemo(() => {
    const byId = new Map<string, PageTreeNode>()
    const childrenByParent = new Map<string | null, PageTreeNode[]>()

    for (const page of pages) {
      byId.set(page.id, page)
      const parentKey = page.parentId ?? null
      if (!childrenByParent.has(parentKey)) {
        childrenByParent.set(parentKey, [])
      }
      childrenByParent.get(parentKey)!.push(page)
    }

    return { byId, childrenByParent }
  }, [pages])

  // Current-language title (fallback first profile, fallback code)
  const getTitle = (page: PageTreeNode) => {
    const profile =
      page.page_profiles.find(
        p => p.language.toLowerCase() === currentLanguage.toLowerCase()
      ) || page.page_profiles[0]
    return profile?.title || page.page_profiles[0]?.title || page.code
  }

  // Current-language slug (own slug only, fallback first profile slug)
  const getSlug = (page: PageTreeNode) => {
    const profile =
      page.page_profiles.find(
        p => p.language.toLowerCase() === currentLanguage.toLowerCase()
      ) || page.page_profiles[0]
    return profile?.slug || page.page_profiles[0]?.slug || ''
  }

  // Roots = no parent OR parent not present in the set (orphans become roots)
  const roots: PageTreeNode[] = useMemo(() => {
    return pages.filter(p => !p.parentId || !byId.has(p.parentId))
  }, [pages, byId])

  // Sort siblings by arrangement, then current-language title
  const sortSiblings = (nodes: PageTreeNode[]): PageTreeNode[] => {
    return [...nodes].sort((a, b) => {
      if (a.arrangement !== b.arrangement) return a.arrangement - b.arrangement
      return getTitle(a).localeCompare(getTitle(b))
    })
  }

  // Full nested route: walk parent chain to root, collect slugs root -> self
  const getRoute = (page: PageTreeNode): string => {
    const segments: string[] = []
    const visited = new Set<string>()
    let current: PageTreeNode | undefined = page

    while (current) {
      if (visited.has(current.id)) break
      visited.add(current.id)
      const slug = getSlug(current)
      segments.unshift(slug || current.code || current.id)
      const parentId = current.parentId ?? null
      current = parentId ? byId.get(parentId) : undefined
    }

    return '/' + segments.join('/')
  }

  // Expand all pages by default when data loads
  useEffect(() => {
    if (pages.length > 0 && expanded.size === 0) {
      setExpanded(new Set(pages.map(p => p.id)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages])

  const toggle = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const term = searchTerm.trim().toLowerCase()
  const isFiltering = term.length > 0

  // Client-side flat filter: code OR title OR slug contains the term
  const filteredPages: PageTreeNode[] = useMemo(() => {
    if (!isFiltering) return []
    return sortSiblings(
      pages.filter(p =>
        [p.code, getTitle(p), getSlug(p)].some(field =>
          field.toLowerCase().includes(term)
        )
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, term, isFiltering, currentLanguage])

  const renderRow = (page: PageTreeNode, depth: number): React.ReactNode => {
    const children = sortSiblings(childrenByParent.get(page.id) ?? [])
    const hasChildren = children.length > 0
    const isExpanded = expanded.has(page.id)

    return (
      <React.Fragment key={page.id}>
        <div
          className="grid gap-2 px-4 py-2.5 border-b hover:bg-muted/30 cursor-pointer items-center"
          style={{ gridTemplateColumns: GRID_COLUMNS }}
          onClick={() => navigate(`/pages/${page.id}`)}
        >
          <div
            className="flex items-center gap-1 min-w-0"
            style={{ paddingLeft: depth * 20 }}
          >
            {hasChildren ? (
              <button
                onClick={e => toggle(page.id, e)}
                className="flex items-center justify-center h-6 w-6 rounded hover:bg-muted shrink-0"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <span className="h-6 w-6 shrink-0" />
            )}
            <span className="font-mono text-sm truncate">{page.code}</span>
          </div>
          <div className="text-sm font-medium truncate">{getTitle(page)}</div>
          <div className="text-sm text-muted-foreground truncate">
            /{getSlug(page)}
          </div>
          <div className="font-mono text-xs text-muted-foreground truncate">
            {getRoute(page)}
          </div>
          <div>
            <Badge variant={page.published ? 'default' : 'secondary'}>
              {page.published ? 'Published' : 'Draft'}
            </Badge>
          </div>
        </div>

        {hasChildren &&
          isExpanded &&
          children.map(child => renderRow(child, depth + 1))}
      </React.Fragment>
    )
  }

  const renderFlatRow = (page: PageTreeNode): React.ReactNode => {
    return (
      <div
        key={page.id}
        className="grid gap-2 px-4 py-2.5 border-b hover:bg-muted/30 cursor-pointer items-center"
        style={{ gridTemplateColumns: GRID_COLUMNS }}
        onClick={() => navigate(`/pages/${page.id}`)}
      >
        <div className="flex items-center gap-1 min-w-0">
          <span className="h-6 w-6 shrink-0" />
          <span className="font-mono text-sm truncate">{page.code}</span>
        </div>
        <div className="text-sm font-medium truncate">{getTitle(page)}</div>
        <div className="text-sm text-muted-foreground truncate">
          /{getSlug(page)}
        </div>
        <div className="font-mono text-xs text-muted-foreground truncate">
          {getRoute(page)}
        </div>
        <div>
          <Badge variant={page.published ? 'default' : 'secondary'}>
            {page.published ? 'Published' : 'Draft'}
          </Badge>
        </div>
      </div>
    )
  }

  const hasContent = pages.length > 0

  return (
    <div className="rounded-md border">
      {/* Table header */}
      <div
        className="grid gap-2 px-4 py-3 bg-muted/50 text-xs font-medium text-muted-foreground border-b"
        style={{ gridTemplateColumns: GRID_COLUMNS }}
      >
        <div>Code</div>
        <div>Title</div>
        <div>Slug</div>
        <div>Route</div>
        <div>Status</div>
      </div>

      {/* Table body */}
      {loading && !hasContent ? (
        <div className="p-4 space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : !hasContent ? (
        <div className="p-8 text-center text-muted-foreground text-sm">
          No pages yet. Create a page to get started.
        </div>
      ) : isFiltering ? (
        filteredPages.length > 0 ? (
          <div>{filteredPages.map(renderFlatRow)}</div>
        ) : (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No pages match &quot;{searchTerm}&quot;.
          </div>
        )
      ) : (
        <div>{sortSiblings(roots).map(page => renderRow(page, 0))}</div>
      )}
    </div>
  )
}
